'use client';

import React, { ChangeEvent, Fragment, useCallback, useMemo, useRef } from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { UnitsMultilevelList } from '@/types';
import { COLORS, COUNTRIES_ORDER } from '@/constants';
import { ListRoot, NodeRow, ChildrenBlock } from './styled';

interface WlasLocationPickerProps {
  unitList?: UnitsMultilevelList;
  value: string[] | null;
  onChange: (value: string[] | null) => void;
}

type UnitNode = {
  code: string;
  title: string;
  children?: UnitNode[];
};

function findNode(nodes: UnitNode[], code: string): UnitNode | undefined {
  for (const n of nodes) {
    if (n.code === code) return n;
    if (n.children) {
      const found = findNode(n.children, code);
      if (found) return found;
    }
  }
}

function getAllDescendantCodes(node: UnitNode): string[] {
  const codes: string[] = [];
  for (const child of node.children ?? []) {
    codes.push(child.code);
    codes.push(...getAllDescendantCodes(child));
  }
  return codes;
}

export const WlasLocationPicker: React.FC<WlasLocationPickerProps> = ({
  unitList,
  value,
  onChange,
}) => {
  const flatOrder = useRef(COUNTRIES_ORDER.flat()).current;

  const countries = useMemo((): UnitNode[] => {
    if (!unitList) return [];
    const all = unitList as UnitNode[];
    const known = COUNTRIES_ORDER.flat()
      .map((code) => all.find((c) => c.code === code))
      .filter((c): c is UnitNode => Boolean(c));
    const other = all.filter((c) => !flatOrder.includes(c.code));
    return [...known, ...other];
  }, [unitList, flatOrder]);

  const handleChange = useCallback(
    (code: string) => (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const checked = event.target.checked;
      const current = [...(value ?? [])];
      const parts = code.split('.');

      if (checked) {
        if (!current.includes(code)) current.push(code);
        // auto-add parents
        if (parts.length === 3) {
          const p2 = `${parts[0]}.${parts[1]}`;
          if (!current.includes(p2)) current.push(p2);
          if (!current.includes(parts[0])) current.push(parts[0]);
        } else if (parts.length === 2) {
          if (!current.includes(parts[0])) current.push(parts[0]);
        }
        // auto-add all descendants
        const node = findNode(countries, code);
        if (node) {
          for (const desc of getAllDescendantCodes(node)) {
            if (!current.includes(desc)) current.push(desc);
          }
        }
        onChange(current);
      } else {
        let updated = current.filter((c) => c !== code);
        if (parts.length === 2) {
          updated = updated.filter((item) => !item.startsWith(`${parts[0]}.${parts[1]}.`));
        } else if (parts.length === 1) {
          updated = updated.filter((item) => !item.startsWith(`${parts[0]}.`));
        }
        onChange(updated.length ? updated : null);
      }
    },
    [value, onChange, countries],
  );

  const CheckboxNode: React.FC<{ node: UnitNode; level?: number }> = ({ node, level = 0 }) => {
    const checked = value !== null && value.includes(node.code);

    return (
      <>
        <NodeRow $level={level}>
          <FormControlLabel
            label={
              <Typography fontSize={14} color={COLORS['primary/900']}>
                {node.title}
              </Typography>
            }
            control={
              <Checkbox
                size="small"
                checked={checked}
                onChange={handleChange(node.code)}
                onClick={(e) => e.stopPropagation()}
              />
            }
          />
        </NodeRow>

        {node.children && node.children.length > 0 && checked && (
          <ChildrenBlock>
            {node.children.map((child) => (
              <Fragment key={child.code}>
                <CheckboxNode node={child} level={level + 1} />
              </Fragment>
            ))}
          </ChildrenBlock>
        )}
      </>
    );
  };

  return (
    <ListRoot>
      {countries.map((country) => (
        <Fragment key={country.code}>
          <CheckboxNode node={country} level={0} />
        </Fragment>
      ))}
    </ListRoot>
  );
};
